import { useLayoutEffect, useState } from "react";
import { TbX } from "react-icons/tb";
import { PageAlertType, usePageAlert } from "../../../Hooks/usePageAlert";

export interface PageAlertRendererProps {
  /** Higher priority wins when multiple renderers are mounted. */
  priority?: number;
  className?: string;
}

const VARIANT_STYLES: Record<
  PageAlertType,
  { container: string; description: string; link: string; dismiss: string }
> = {
  success: {
    container: "border-emerald-200 bg-emerald-50 text-emerald-900",
    description: "text-emerald-800/90",
    link: "text-emerald-900",
    dismiss: "text-emerald-700 hover:bg-emerald-100/80 hover:text-emerald-900",
  },
  error: {
    container: "border-red-200 bg-red-50 text-red-900",
    description: "text-red-800/90",
    link: "text-red-900",
    dismiss: "text-red-700 hover:bg-red-100/80 hover:text-red-900",
  },
};

export const PageAlertRenderer = ({
  priority = 0,
  className = "mb-4",
}: PageAlertRendererProps) => {
  const { alert, dismissPageAlert, registerRenderer } = usePageAlert();
  const [isActive, setIsActive] = useState(false);

  useLayoutEffect(() => registerRenderer(priority, setIsActive), [priority, registerRenderer]);

  if (!isActive || !alert) {
    return null;
  }

  const variant = alert.type ?? "success";
  const styles = VARIANT_STYLES[variant];

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={`flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-sm ${styles.container} ${className}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold leading-5">{alert.title}</p>
        {alert.description && (
          <p className={`mt-1 text-sm leading-5 ${styles.description}`}>{alert.description}</p>
        )}
        {alert.linkTo && (
          <a
            href={alert.linkTo}
            className={`mt-1 inline-block font-semibold underline ${styles.link}`}
          >
            {alert.linkLabel || "Ver detalhes"}
          </a>
        )}
      </div>

      <button
        type="button"
        onClick={() => dismissPageAlert(alert.id)}
        className={`shrink-0 rounded-md p-1 transition ${styles.dismiss}`}
        aria-label="Fechar alerta"
      >
        <TbX className="h-4 w-4" strokeWidth={2.25} />
      </button>
    </div>
  );
};
